import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, ArrowUpRight } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";

const LINKS = [
  { to: "/patients-families", label: "Patients & Families", testid: "nav-link-patients" },
  { to: "/professionals", label: "Professionals", testid: "nav-link-professionals" },
  { to: "/about", label: "About", testid: "nav-link-about" },
  { to: "/services", label: "Services", testid: "nav-link-services" },
  { to: "/careers", label: "Careers", testid: "nav-link-careers" },
  { to: "/contact", label: "Contact", testid: "nav-link-contact" },
];

export function Wordmark({ dark = false }) {
  return (
    <Link to="/" data-testid="nav-logo" className="group flex items-center gap-3">
      <img
        src={dark ? "/assets/logo-mark-light.png" : "/assets/logo-mark.png"}
        alt="Homevia Transitional Care logo"
        className="h-11 w-auto"
      />
      <span className="flex flex-col leading-none">
        <span className={`font-serif text-2xl font-semibold tracking-tight ${dark ? "text-cream" : "text-ink"}`}>
          Homevia
        </span>
        <span className={`text-[0.6rem] font-bold uppercase tracking-[0.28em] ${dark ? "text-cream/60" : "text-teal"}`}>
          Transitional Care
        </span>
      </span>
    </Link>
  );
}

export default function Navbar() {
  const [open, setOpen] = useState(false);
  return (
    <header
      data-testid="site-header"
      className="fixed inset-x-0 top-0 z-50 border-b border-olive/10 bg-cream/70 backdrop-blur-xl"
    >
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-6 lg:px-10">
        <Wordmark />
        <nav className="hidden items-center gap-8 lg:flex" aria-label="Primary">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.testid}
              className={({ isActive }) =>
                `text-sm font-semibold transition-colors duration-300 hover:text-teal-deep ${
                  isActive ? "text-teal-deep underline underline-offset-8 decoration-sage decoration-2" : "text-olive/80"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <Link
            to="/request-care"
            data-testid="nav-request-care-btn"
            className="hidden items-center gap-2 rounded-full bg-teal-deep px-5 py-2.5 text-sm font-bold text-cream transition-all duration-300 hover:bg-olive hover:shadow-lg sm:inline-flex"
          >
            Request Care
            <ArrowUpRight className="h-4 w-4" />
          </Link>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="nav-mobile-menu-btn"
                aria-label="Open menu"
                className="grid h-11 w-11 place-items-center rounded-full border border-olive/15 text-olive transition-colors hover:bg-sage/10 lg:hidden"
              >
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[320px] bg-cream p-8">
              <SheetTitle className="sr-only">Menu</SheetTitle>
              <div className="mt-6 flex flex-col gap-6">
                {LINKS.map((l) => (
                  <Link
                    key={l.to}
                    to={l.to}
                    data-testid={`mobile-${l.testid}`}
                    onClick={() => setOpen(false)}
                    className="font-serif text-2xl text-ink transition-colors hover:text-teal-deep"
                  >
                    {l.label}
                  </Link>
                ))}
                <Link
                  to="/request-care"
                  data-testid="mobile-nav-request-care-btn"
                  onClick={() => setOpen(false)}
                  className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-teal-deep px-6 py-3 text-sm font-bold text-cream"
                >
                  Request Care <ArrowUpRight className="h-4 w-4" />
                </Link>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
